<script lang="ts">
  import Icon from './Icon.svelte';
  import type { SessionDTO } from '../lib/types';
  import { daysUntil, daysWord, formatDate } from '../lib/format';
  import { isExpiringSoon } from '../lib/session';
  import { TG_SUB_LINK } from '../lib/bot-links';
  import { openBotLink } from '../lib/telegram';

  interface Props {
    session: SessionDTO;
  }
  let { session }: Props = $props();

  function greeting(): string {
    const h = new Date().getHours();
    if (h < 5) return 'Доброї ночі,';
    if (h < 12) return 'Доброго ранку,';
    if (h < 18) return 'Доброго дня,';
    if (h < 23) return 'Доброго вечора,';
    return 'Доброї ночі,';
  }
  const hello = greeting();

  const endDate = $derived(
    session.subscriptionEndDate ? new Date(session.subscriptionEndDate) : null,
  );
  const endDateFmt = $derived(formatDate(endDate));
  const remainingDays = $derived(endDate ? daysUntil(endDate) : 0);
  const remainingWord = $derived(daysWord(remainingDays));
  const initial = $derived(session.name.charAt(0).toUpperCase());
  // "Expiring soon" is a UI cue, not a status — active subscription near its end.
  const expiringSoon = $derived(
    session.status === 'active' && isExpiringSoon(session.subscriptionEndDate),
  );
</script>

<div class="home-banner">
  <div class="banner-row">
    <div class="banner-avatar">
      {#if session.photo}
        <img src={session.photo} alt="" />
      {:else}
        {initial}
      {/if}
    </div>
    <div>
      <div class="banner-hi">{hello}</div>
      <div class="banner-name">{session.name}</div>
    </div>
  </div>

  {#if session.status === 'active' && expiringSoon}
    <div class="sub-bar sub-bar-warn">
      <div>
        <div class="sub-bar-text"><Icon name="triangle-alert" size="xs" /> Закінчується скоро</div>
        <div class="sub-bar-date">
          до {endDateFmt} · ще {remainingDays} {remainingWord}
        </div>
      </div>
      <a href={TG_SUB_LINK} target="_blank" rel="noopener" class="sub-bar-btn" onclick={(e) => openBotLink(e, TG_SUB_LINK)}>Продовжити</a>
    </div>
  {:else if session.status === 'active'}
    <div class="sub-bar sub-bar-success">
      <div>
        <div class="sub-bar-text">Підписка активна</div>
        <div class="sub-bar-date">до {endDateFmt}</div>
      </div>
      <a href={TG_SUB_LINK} target="_blank" rel="noopener" class="sub-bar-btn" onclick={(e) => openBotLink(e, TG_SUB_LINK)}>Продовжити</a>
    </div>
  {:else if session.status === 'inactive'}
    <div class="sub-bar sub-bar-danger">
      <div>
        <div class="sub-bar-text">Підписка закінчилась</div>
        <div class="sub-bar-date">{endDateFmt}</div>
      </div>
      <a href={TG_SUB_LINK} target="_blank" rel="noopener" class="sub-bar-btn" onclick={(e) => openBotLink(e, TG_SUB_LINK)}>Поновити</a>
    </div>
  {:else}
    <div class="sub-bar sub-bar-muted">
      <div>
        <div class="sub-bar-text">Без підписки</div>
        <div class="sub-bar-date">Оформіть, щоб відкрити доступ</div>
      </div>
      <a href={TG_SUB_LINK} target="_blank" rel="noopener" class="sub-bar-btn" onclick={(e) => openBotLink(e, TG_SUB_LINK)}>Оформити</a>
    </div>
  {/if}
</div>

<style>
  .home-banner {
    background: linear-gradient(140deg, #12100e 0%, var(--dark) 55%, var(--dark3) 100%);
    padding: 18px;
    color: var(--text-light);
    border-radius: 16px;
    margin-bottom: 18px;
    position: relative;
    overflow: hidden;
    /* iOS WebKit sometimes doesn't clip absolute-positioned descendants
       to the parent's border-radius via overflow:hidden alone. clip-path
       forces a deterministic rounded-rect clip that matches the radius. */
    -webkit-clip-path: inset(0 round 16px);
    clip-path: inset(0 round 16px);
    isolation: isolate;
  }
  .home-banner::after {
    content: '';
    position: absolute;
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: rgba(251, 246, 236, 0.05);
    right: -20px;
    top: -20px;
  }
  .banner-hi {
    font-size: 11px;
    opacity: 0.6;
    margin-bottom: 3px;
  }
  .banner-name {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 6px;
  }
  .banner-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 6px;
    position: relative;
    z-index: 1;
  }
  .banner-avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 19px;
    font-weight: 600;
    color: var(--text-light);
    flex-shrink: 0;
    border: 2px solid rgba(251, 246, 236, 0.25);
    overflow: hidden;
  }
  .banner-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .sub-bar {
    background: rgba(251, 246, 236, 0.1);
    border-radius: 10px;
    padding: 10px 12px;
    margin-top: 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .sub-bar-text {
    font-size: 11px;
    opacity: 0.7;
  }
  .sub-bar-date {
    font-size: 11px;
    font-weight: 600;
  }
  .sub-bar-btn {
    font-size: 10px;
    background: rgba(251, 246, 236, 0.2);
    border: none;
    color: var(--text-light);
    padding: 4px 10px;
    border-radius: 7px;
    cursor: pointer;
    font-weight: 500;
    text-decoration: none;
  }
  /* Банер завжди темний, тож статуси беруть НІЧНІ значення токенів явно:
     денні (Шавлія #5C6D5A, Глина #A8502F) на цьому тлі не читаються.
     Значення — ті самі, що в мокапі для .sub-bar-*. */
  .sub-bar-success .sub-bar-text,
  .sub-bar-success .sub-bar-date {
    color: #8fa98b;
    opacity: 1;
  }
  .sub-bar-warn .sub-bar-text,
  .sub-bar-warn .sub-bar-date {
    color: #e8a13d;
    opacity: 1;
  }
  .sub-bar-danger .sub-bar-text,
  .sub-bar-danger .sub-bar-date {
    color: #d98a6a;
    opacity: 1;
  }
  .sub-bar-muted {
    background: rgba(251, 246, 236, 0.06);
  }
</style>
